import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";

import style from "./view-delivery-history.scss";
import PreviewIcon from "components/icons/PreviewIcon";
import BundleTokenPricingView from "components/licenses/BundleTokenPricingView";
import { createLicenseTypes, getDeliveryTypes } from "components/licenses/common";
import tokenStyle from "components/licenses/delivery-history/add-license-delivery.scss";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import StaticTable from "components/support/api-guide/StaticTable";
import DateCell from "components/table/DateCell";
import { deduceTierLicenses } from "components/tenants/add-tenant/AddTenantForm";
import { DeliveryStatus, LicenseDeliveryHistory } from "domain/licenses";
import { DeliveryMode } from "domain/licenses";
import { CONTRACT_TYPE_TRIAL, TenantType } from "domain/tenants";
import {
    extractProductRatesList,
    extractTenantLicenseRateUuid,
    fetchLicenseRateVersion,
    ProductToRateList,
} from "services/licenses/LicenseService";
import { isLicensingModelBundleWithToken, isUserParentInternal } from "services/tenants/tenantCookieService";
import { replaceProductIdWithNameInRatesTable } from "store/license";
import buttons from "styles/buttons.scss";
import form from "styles/form.scss";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

export function createProductsMap(): Map<string, string> {
    const licenses = createLicenseTypes(true);
    return new Map<string, string>(licenses.map((each) => [each.productId, each.productName]));
}

export function fetchRateVersionNameFromUuid(rateUuid: string | undefined, tokenRatesLists: ProductToRateList[]) {
    let rateVersionName = "";
    for (const rate of tokenRatesLists) {
        if (rate.uuid === rateUuid) {
            rateVersionName = rate.name;
        }
    }
    return rateVersionName;
}

function getDeliveryStatusLabel(status: string | undefined, t: (key: string) => string): string {
    switch (status) {
        case DeliveryStatus.SCHEDULED:
            return t("DeliveryHistory.deliveryHistoryTable.statusFilter.scheduled");
        case DeliveryStatus.DELIVERED:
            return t("DeliveryHistory.deliveryHistoryTable.statusFilter.delivered");
        case DeliveryStatus.ON_HOLD:
            return t("DeliveryHistory.deliveryHistoryTable.statusFilter.onHold");
        case DeliveryStatus.TRIGGERED_MANUALLY:
            return t("DeliveryHistory.deliveryHistoryTable.statusFilter.triggered_manually");
        case DeliveryStatus.FAILED:
            return t("DeliveryHistory.deliveryHistoryTable.statusFilter.failed");
        case DeliveryStatus.DELETED:
            return t("DeliveryHistory.deliveryHistoryTable.statusFilter.deleted");
        default:
            return status || t("DeliveryHistory.deliveryHistoryTable.statusFilter.delivered");
    }
}

export const ViewDeliveryHistory = (props: {
    deliveryHistoryDetails: LicenseDeliveryHistory;
    loading: boolean;
    tenantTier: string;
    tenantType: TenantType;
}): JSX.Element => {
    const { t } = useTranslation();
    const DELIVERY_TYPES = getDeliveryTypes();
    const ALL_PRODUCTS = createProductsMap();
    const [bundleTokenPricingModalVisible, setBundleTokenPricingModalVisible] = React.useState(false);
    const theme = useTheme();
    const deducedTierLicenses = deduceTierLicenses(props.tenantTier, true, true);
    const [tokenRateVersions, setTokenRateVersions] = React.useState<ProductToRateList[]>([]);
    const [rateUuid, setTokenRateUUid] = React.useState<string>("");

    async function fetchRatesFromApi() {
        const rates = await fetchLicenseRateVersion();
        setTokenRateVersions(rates);
        setTokenRateUUid(extractTenantLicenseRateUuid(rates, props.tenantType));
    }
    React.useEffect(() => {
        fetchRatesFromApi();
    }, []);

    return props.loading ? (
        <div>
            <LoadingIndicator />
        </div>
    ) : (
        <>
            <div className={style.deliveryHistoryDetails}>
                {props.deliveryHistoryDetails.type && (
                    <>
                        <div className={style.label}>{t("DeliveryHistory.deliveryHistoryDialog.type")}</div>
                        <div
                            data-testid={testIds.workArea.license.deliveryHistory.viewDeliveryDialog.deliveryTypeLabel}
                        >
                            {props.deliveryHistoryDetails.type === CONTRACT_TYPE_TRIAL
                                ? t("DeliveryHistory.deliveryType.trial")
                                : DELIVERY_TYPES.get(props.deliveryHistoryDetails.type)}
                        </div>
                    </>
                )}

                <>
                    <div className={style.label}>{t("DeliveryHistory.deliveryHistoryDialog.deliverySchedule")}</div>
                    <div
                        data-testid={testIds.workArea.license.deliveryHistory.viewDeliveryDialog.deliveryScheduleLabel}
                    >
                        {props.deliveryHistoryDetails.targetDate ? (
                            <DateCell
                                tooltip={false}
                                value={props.deliveryHistoryDetails.targetDate}
                                withoutTime={true}
                            />
                        ) : (
                            "-"
                        )}
                    </div>
                </>

                <div className={style.label}>{t("DeliveryHistory.deliveryHistoryDialog.deliveryStatus")}</div>
                <div data-testid={testIds.workArea.license.deliveryHistory.viewDeliveryDialog.deliveryStatusLabel}>
                    {getDeliveryStatusLabel(props.deliveryHistoryDetails.status, t)}
                </div>

                {props.deliveryHistoryDetails.opportunityId && isUserParentInternal() && (
                    <>
                        <div className={style.label}>{t("DeliveryHistory.deliveryHistoryDialog.opportunityId")}</div>
                        <div
                            data-testid={testIds.workArea.license.deliveryHistory.viewDeliveryDialog.opportunityIdLabel}
                        >
                            {props.deliveryHistoryDetails.opportunityId}
                        </div>
                    </>
                )}

                {props.deliveryHistoryDetails.caseNumber && isUserParentInternal() && (
                    <>
                        <div className={style.label}>{t("DeliveryHistory.deliveryHistoryDialog.caseNumber")}</div>
                        <div data-testid={testIds.workArea.license.deliveryHistory.viewDeliveryDialog.caseNumberLabel}>
                            {props.deliveryHistoryDetails.caseNumber}
                        </div>
                    </>
                )}

                {props.deliveryHistoryDetails.creationDate && (
                    <>
                        <div className={style.label}>{t("DeliveryHistory.deliveryHistoryDialog.creationDate")}</div>
                        <div
                            data-testid={testIds.workArea.license.deliveryHistory.viewDeliveryDialog.creationDateLabel}
                        >
                            <DateCell
                                tooltip={false}
                                value={props.deliveryHistoryDetails.creationDate}
                                withoutTime={true}
                            />
                        </div>
                    </>
                )}

                {isUserParentInternal() && (
                    <>
                        <div className={style.label}>{t("DeliveryHistory.deliveryHistoryDialog.editDate")}</div>
                        <div data-testid={testIds.workArea.license.deliveryHistory.viewDeliveryDialog.editDateLabel}>
                            {props.deliveryHistoryDetails.editDate &&
                            props.deliveryHistoryDetails.editDate.trim() !== "" ? (
                                <DateCell tooltip={false} value={props.deliveryHistoryDetails.editDate} />
                            ) : (
                                "-"
                            )}
                        </div>
                    </>
                )}

                {props.deliveryHistoryDetails.creatorUuid && isUserParentInternal() && (
                    <>
                        <div className={style.label}>{t("DeliveryHistory.deliveryHistoryDialog.creatorUuid")}</div>
                        <div data-testid={testIds.workArea.license.deliveryHistory.viewDeliveryDialog.creatorUuidLabel}>
                            {props.deliveryHistoryDetails.creatorUuid}
                        </div>
                    </>
                )}

                {isUserParentInternal() && (
                    <>
                        <div className={style.label}>{t("DeliveryHistory.deliveryHistoryDialog.editorUuid")}</div>
                        <div data-testid={testIds.workArea.license.deliveryHistory.viewDeliveryDialog.editorUuidLabel}>
                            {props.deliveryHistoryDetails.editorUuid &&
                            props.deliveryHistoryDetails.editorUuid.trim() !== ""
                                ? props.deliveryHistoryDetails.editorUuid
                                : "-"}
                        </div>
                    </>
                )}

                {props.deliveryHistoryDetails.licenseRateVersion &&
                    isUserParentInternal() &&
                    isLicensingModelBundleWithToken() && (
                        <>
                            <div className={style.label}>
                                {t("DeliveryHistory.deliveryHistoryDialog.licenseRateVersion")}
                            </div>
                            <div className={form.formFields}>
                                <div
                                    data-testid={
                                        testIds.workArea.license.deliveryHistory.viewDeliveryDialog
                                            .licenseRateVersionLabel
                                    }
                                >
                                    {props.deliveryHistoryDetails.licenseRateVersion}
                                </div>
                                <div>
                                    <button
                                        className={classNames(tokenStyle.tooltipPosition, buttons.textButton)}
                                        type="button"
                                        onClick={() => {
                                            setBundleTokenPricingModalVisible(true);
                                        }}
                                    >
                                        <div>
                                            <PreviewIcon color={theme.iconFillColor} />
                                            <span className={tokenStyle.addMoreButton}>
                                                {t("DeliveryHistory.addLicenseDelivery.previewRateTable")}
                                            </span>
                                        </div>
                                    </button>
                                </div>
                                <BundleTokenPricingView
                                    visibility={bundleTokenPricingModalVisible}
                                    setVisibility={setBundleTokenPricingModalVisible}
                                    productToRate={replaceProductIdWithNameInRatesTable(
                                        extractProductRatesList(tokenRateVersions, rateUuid),
                                        deducedTierLicenses
                                    )}
                                />
                            </div>
                        </>
                    )}

                {props.deliveryHistoryDetails.notes && (
                    <>
                        <div className={style.label}>{t("DeliveryHistory.deliveryHistoryDialog.notes")}</div>
                        <div data-testid={testIds.workArea.license.deliveryHistory.viewDeliveryDialog.notesLabel}>
                            {props.deliveryHistoryDetails.notes}
                        </div>
                    </>
                )}
            </div>
            <StaticTable
                headers={[
                    {
                        value: t("DeliveryHistory.deliveryHistoryDialog.table.licenseType"),
                    },
                    {
                        value: t("DeliveryHistory.deliveryHistoryDialog.table.deliveryMode"),
                    },
                    {
                        value: t("DeliveryHistory.deliveryHistoryDialog.table.totalOfLicenses"),
                    },
                    {
                        value: t("DeliveryHistory.deliveryHistoryDialog.table.expirationDate"),
                    },
                ]}
                cells={
                    props.deliveryHistoryDetails.licenses.length > 0
                        ? props.deliveryHistoryDetails.licenses.map((license, key) => {
                              return [
                                  <div
                                      data-testid={
                                          testIds.workArea.license.deliveryHistory.viewDeliveryDialog.table
                                              .licenseTypeLabel
                                      }
                                      key={license.licenseId}
                                      className={style.label}
                                  >
                                      {ALL_PRODUCTS.get(license.licenseId)}
                                  </div>,
                                  <div key={"deliveryMode" + key} className={style.label}>
                                      {license.deliveryMode === DeliveryMode.SET
                                          ? t("DeliveryHistory.addLicenseDelivery.table.set")
                                          : t("DeliveryHistory.addLicenseDelivery.table.add")}
                                  </div>,
                                  <div
                                      data-testid={
                                          testIds.workArea.license.deliveryHistory.viewDeliveryDialog.table
                                              .totalOfLicensesLabel
                                      }
                                      key={"amount" + key}
                                      className={style.label}
                                  >
                                      {license.amount}
                                  </div>,
                                  <div key={"date" + key} className={style.label}>
                                      <DateCell
                                          tooltip={false}
                                          value={license.expirationDate}
                                          testId={
                                              testIds.workArea.license.deliveryHistory.viewDeliveryDialog.table
                                                  .expirationDateLabel
                                          }
                                      />
                                  </div>,
                              ];
                          })
                        : []
                }
                testId={testIds.workArea.license.deliveryHistory.viewDeliveryDialog.table.itself}
                emptyStateMessage={t("LicenseEvent.filter.noDataAvailable")}
            />
        </>
    );
};
